import { CaseResource } from '@api/types/case/case.resource';
import SectionScaffold from '@components/outcome-report/components/section-scaffold';
import { styled } from '@mui/material';
import Column from '@components/layout-util-components/column';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBuildings,
  faLocationDot,
} from '@fortawesome/pro-light-svg-icons';
import { getAddressString, isNullOrEmpty } from '@util/string-util';
import Row from '@components/layout-util-components/row';
import {
  dateOnlyShortFormatter,
  timeOnlyShortFormatter,
} from '@i18n/formatters';
import { GOOGLE_MAP_KEY } from '@util/env';
import { useEffect, useRef, useState } from 'react';
import { BlackButton } from '@components/outcome-report/components/black-button';
import useSystemText from '@hooks/use-system-text';
import { Gap } from '@components/layout-util-components/gap';

interface Props {
  caseDetails: CaseResource;
}

// Grid layout split into 2 columns,
// the first column takes up 1/3 of the space and contains 2 rows (overview and dates)
// the second column up 2/3 of the space and has 1 row (map)
const Container = styled('div')(({ theme: { palette } }) => ({
  display: 'grid',

  gridTemplateAreas: `
    'overview map'
    'dates map'
  `,
  columnGap: 0,
  rowGap: 0,
  height: '100%',
  width: '100%',
  backgroundColor: palette.common.grey15,

  '.overview': {
    gridArea: 'overview',
    backgroundColor: '#FFF9EF',
    padding: 64,
  },

  '.dates': {
    gridArea: 'dates',
    backgroundColor: palette.common.white,
    padding: 64,
  },

  '.map': {
    position: 'relative',
    gridArea: 'map',
    backgroundColor: 'teal',
    overflow: 'hidden',

    '& > img': {
      width: '100%',
      height: '100%',

      objectFit: 'cover',
      objectPosition: 'center',
    },

    '.open-map-link': {
      position: 'absolute',
      right: 50,
      bottom: 50,
      borderRadius: '5px',
      padding: '15px 25px',
      background: '#00223A',
    },
  },

  '.title': {
    fontSize: '32px',
    fontWeight: '400',
    lineHeight: '30px',
    letterSpacing: '0.15000000596046448px',
    textAlign: 'left',
  },

  '.data': {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 14,

    '.label': {
      lineHeight: '19px',
      letterSpacing: '0px',
      textAlign: 'left',
      fontSize: '13px',
      textTransform: 'uppercase',
      fontWeight: '500',
      color: '#949FA2',
    },

    '.value': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      fontSize: '20px',
      fontWeight: '400',
      lineHeight: '27px',
      letterSpacing: '0px',
      textAlign: 'left',
      color: palette.common.black,
      svg: {
        color: '#FF6C00',
      },
      '&.reporting svg': {
        color: '#00223A',
      },
    },

    '&.large': {
      gap: 17,

      '.label': {
        lineHeight: '19px',
        letterSpacing: '0px',
        textAlign: 'left',
        fontSize: '13px',
        textTransform: 'uppercase',
        fontWeight: '500',
        color: '#949FA2',
      },

      '.value': {
        fontSize: '26px',
        lineHeight: '29px',
      },
    },
  },
  '.dates .custom-row': {
    '> *': {
      flex: '0 0 50%',
    },
  },
}));

export default function OverviewSection({ caseDetails }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapImageUrl, setMapImageUrl] = useState<string>();
  const [mapUrl, setMapUrl] = useState<string>();
  const { systemText } = useSystemText();

  const {
    focalPoint,
    incidentLocation,
    reportingLocation,
    startDate,
    endDate,
    frequencyTypeName,
    frequencyValue,
  } = caseDetails;

  useEffect(() => {
    const location = incidentLocation ?? reportingLocation;
    const { latitude, longitude } = location;

    const width = mapRef.current!.clientWidth;
    const height = mapRef.current!.clientHeight;

    // let marker = `&markers=color:red%7Clabel:%7C${latitude},${longitude}`;
    // set marker pin to MapPin

    const mapPinUrl = window.location.origin + '/map-pin.png';
    const marker = `anchor:bottom|icon:${mapPinUrl}|${latitude},${longitude}`;
    const googleMapsImageUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=9&size=${
      width * 2
    }x${height * 2}&scale=2&maptype=roadmapp&markers=${encodeURIComponent(
      marker
    )}&key=${GOOGLE_MAP_KEY}`;
    setMapImageUrl(googleMapsImageUrl);

    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    setMapUrl(mapUrl);
  }, [incidentLocation, reportingLocation]);

  return (
    <SectionScaffold id="overview">
      <Container>
        <div className="overview">
          <Column>
            <div className="title">Overview</div>
            <Gap size={32} />
            <div className="data large">
              <div className="label">
                {systemText['RCA.Overview.FocalPoint']}
              </div>
              <div className="value">{focalPoint}</div>
            </div>
            {incidentLocation != null ? (
              <>
                <Gap size={32} />
                <div className="data">
                  <div className="label">Incident Location</div>
                  <div className="value">
                    <FontAwesomeIcon icon={faLocationDot} />
                    <span>{getAddressString(incidentLocation!)}</span>
                  </div>
                </div>
              </>
            ) : null}
            {reportingLocation != null ? (
              <>
                <Gap size={32} />
                <div className="data">
                  <div className="label">Reporting Location</div>
                  <div className="value reporting">
                    <FontAwesomeIcon icon={faBuildings} />
                    <span>{getAddressString(reportingLocation!)}</span>
                  </div>
                </div>
              </>
            ) : null}
          </Column>
        </div>
        <div className="dates">
          <Column>
            <Row>
              <div className="data">
                <div className="label">START DATE</div>
                <div className="value">{dateOnlyShortFormatter(startDate)}</div>
              </div>
              {!isNullOrEmpty(endDate) ? (
                <>
                  <div className="data">
                    <div className="label">END DATE</div>
                    <div className="value">
                      {dateOnlyShortFormatter(endDate)}
                    </div>
                  </div>
                </>
              ) : null}
            </Row>
            <Gap size={32} />
            <Row>
              <div className="data">
                <div className="label">START TIME</div>
                <div className="value">{timeOnlyShortFormatter(startDate)}</div>
              </div>
              {!isNullOrEmpty(endDate) ? (
                <>
                  <div className="data">
                    <div className="label">END TIME</div>
                    <div className="value">
                      {timeOnlyShortFormatter(endDate)}
                    </div>
                  </div>
                </>
              ) : null}
            </Row>
            {frequencyValue != null && !isNullOrEmpty(frequencyTypeName) ? (
              <>
                <Gap size={32} />
                <div className="data">
                  <div className="label">Frequency</div>
                  <div className="value">
                    {`${
                      frequencyValue === 1 ? 'Once' : `${frequencyValue} times`
                    } ${frequencyTypeName}`}
                  </div>
                </div>
              </>
            ) : null}
          </Column>
        </div>
        <div ref={mapRef} className="map">
          {mapImageUrl ? (
            <>
              <img src={mapImageUrl} alt="map" />
              <BlackButton
                className="open-map-link"
                target="_blank"
                href={mapUrl}
                rel="noreferrer"
              >
                <span>View Map </span>
                <FontAwesomeIcon icon={faArrowRight} />
              </BlackButton>
            </>
          ) : null}
        </div>
      </Container>
    </SectionScaffold>
  );
}
